import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ShieldCheck, Info, CalendarDays } from 'lucide-react';
import { quoteMoveIn, reserveUnit, listVehicles } from '../../lib/api';
import { formatCents, formatDate } from '../../lib/format';
import { useAuthStore } from '../../store/useAuthStore';

/**
 * Sticky cost calculator.
 *
 * The quote is always fetched from POST /facilities/quote — the client never
 * computes a total, so proration rules, fee policy, and tax rates live in
 * exactly one place and the number shown is the number charged.
 */
export default function QuotePanel({ unit, facility, onReserved }) {
  const isSignedIn = useAuthStore((s) => s.status === 'signed_in');
  const [startsOn, setStartsOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [billingDay, setBillingDay] = useState(1);
  const [quote, setQuote] = useState(null);
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState('');
  const [reserving, setReserving] = useState(false);

  // Only signed-in tenants have vehicles to attach.
  useEffect(() => {
    if (!isSignedIn) return;
    listVehicles().then(({ vehicles: list }) => {
      setVehicles(list);
      setVehicleId((current) => current || list[0]?.public_id || '');
    }).catch(() => setVehicles([]));
  }, [isSignedIn]);

  const reserve = async () => {
    setReserving(true);
    setError(null);
    try {
      const result = await reserveUnit({
        unit_id: unit.public_id,
        vehicle_id: vehicleId || null,
        starts_on: startsOn,
        billing_day: billingDay,
        autopay: true,
      });
      onReserved(result);
    } catch (err) {
      setError(err.message);
      setReserving(false);
    }
  };

  useEffect(() => {
    if (!unit || !startsOn) {
      setQuote(null);
      setState('idle');
      return undefined;
    }

    let cancelled = false;
    // Debounced so dragging a date picker doesn't fire a quote per keystroke.
    const timer = setTimeout(async () => {
      setState('loading');
      setError(null);
      try {
        const result = await quoteMoveIn({
          unit_id: unit.public_id, starts_on: startsOn, billing_day: billingDay,
        });
        if (cancelled) return;
        setQuote(result);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        setState('error');
      }
    }, 300);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [unit, startsOn, billingDay]);

  if (!unit) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center">
        <Info className="mx-auto h-6 w-6 text-brand-300" aria-hidden="true" />
        <p className="mt-2 text-sm font-bold text-slate-900">Pick a unit</p>
        <p className="mt-1 text-xs text-slate-500">
          Select a space to see the move-in cost and your monthly rate.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Unit {unit.unit_number}</p>
      <p className="text-2xl font-extrabold text-slate-900">
        {formatCents(unit.monthly_rate_cents)}
        <span className="text-sm font-medium text-slate-500"> / month</span>
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <label className="field-label mb-1" htmlFor="starts-on">Move-in date</label>
          <input id="starts-on" type="date" className="field-input"
                 min={new Date().toISOString().slice(0, 10)}
                 value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        </div>
        <div>
          <label className="field-label mb-1" htmlFor="billing-day">Bill on the</label>
          <select id="billing-day" className="field-input" value={billingDay}
                  onChange={(e) => setBillingDay(Number(e.target.value))}>
            {[1, 5, 10, 15, 20, 25, 28].map((d) => (
              <option key={d} value={d}>{ordinal(d)}</option>
            ))}
          </select>
        </div>
      </div>

      {state === 'loading' && (
        <p className="mt-4 flex items-center justify-center gap-2 py-4 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Pricing your move-in…
        </p>
      )}

      {state === 'error' && (
        <p className="mt-4 rounded-lg bg-rose-50 p-3 text-xs font-medium text-rose-800">{error}</p>
      )}

      {state === 'ready' && quote && (
        <>
          <dl className="mt-4 space-y-2 border-t border-slate-200 pt-4 text-sm">
            {quote.due_today.line_items.map((item) => (
              <div key={item.kind + item.description} className="flex justify-between gap-4">
                <dt className="text-slate-600">
                  {item.description}
                  {!item.is_taxable && <span className="ml-1 text-[11px] text-slate-400">(not taxed)</span>}
                </dt>
                <dd className="shrink-0 tabular-nums text-slate-900">{formatCents(item.amount_cents)}</dd>
              </div>
            ))}
            <div className="flex justify-between gap-4">
              <dt className="text-slate-600">Tax ({Number(quote.due_today.tax_rate_pct).toFixed(2)}%)</dt>
              <dd className="shrink-0 tabular-nums text-slate-900">{formatCents(quote.due_today.tax_cents)}</dd>
            </div>
          </dl>

          <div className="mt-3 flex items-baseline justify-between border-t border-slate-200 pt-3">
            <span className="text-base font-extrabold text-slate-900">Due today</span>
            <span className="text-xl font-extrabold tabular-nums text-slate-900">
              {formatCents(quote.due_today.total_cents)}
            </span>
          </div>

          {/* The recurring number is what tenants actually compare between
              facilities, so it gets its own block rather than a footnote. */}
          <div className="mt-4 rounded-lg bg-brand-50 p-3">
            <p className="flex items-center gap-1.5 text-xs font-bold text-brand-900">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
              Then {formatCents(quote.recurring.total_cents)}/month
            </p>
            <p className="mt-1 text-[12px] text-brand-800">
              Starting {formatDate(quote.recurring.first_charge_on)}, on the {ordinal(quote.term.billing_day)} of each
              month. Month-to-month — no end date, cancel with notice.
            </p>
            {quote.term.prorated_days > 0 && (
              <p className="mt-1.5 text-[12px] text-brand-800">
                Your first charge is prorated for {quote.term.prorated_days} of{' '}
                {quote.term.days_in_month} days.
              </p>
            )}
          </div>
        </>
      )}

      {isSignedIn && vehicles.length > 0 && (
        <div className="mt-4">
          <label className="field-label mb-1" htmlFor="quote-vehicle">Vehicle to store</label>
          <select id="quote-vehicle" className="field-input" value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}>
            {vehicles.map((v) => (
              <option key={v.public_id} value={v.public_id}>
                {v.label || v.kind.replace(/_/g, ' ')} — {v.length_ft}ft
              </option>
            ))}
          </select>
          <p className="mt-1 text-[12px] text-slate-500">
            We check it fits before confirming.
          </p>
        </div>
      )}

      {isSignedIn ? (
        <button type="button" onClick={reserve} disabled={state !== 'ready' || reserving}
                className="btn-primary mt-4 w-full py-3">
          {reserving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {reserving ? 'Reserving…' : 'Reserve this unit'}
        </button>
      ) : (
        <Link to="/login" state={{ from: window.location.pathname }} className="btn-primary mt-4 w-full py-3">
          Sign in to reserve
        </Link>
      )}

      <p className="mt-3 flex items-center justify-center gap-1.5 text-[12px] text-slate-500">
        <ShieldCheck className="h-3.5 w-3.5 text-brand-600" aria-hidden="true" />
        You won&apos;t be charged yet.
      </p>
    </div>
  );
}

function ordinal(n) {
  const suffix = ['th', 'st', 'nd', 'rd'][(n % 100 - 20) % 10] ?? ['th', 'st', 'nd', 'rd'][n % 100] ?? 'th';
  return `${n}${suffix}`;
}
